import { createHttp, type ApiClientOptions } from './http';
import { tournamentQs } from './tournamentQuery';
import { exportPath, type ExportChoice, type ExportTarget } from './exportPaths';
import type * as T from './types';

const qs = (params: Record<string, string | undefined>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) search.set(key, value);
  }
  const s = search.toString();
  return s ? `?${s}` : '';
};

/**
 * An Overview period (user-preferences.md §2.2): a `preset` or both `from` and `to` (yyyy-MM-dd), never both; none
 * is All time. Resolved by the server at the room's gaming day.
 */
export interface RoomOverviewPeriodParams {
  preset?: DatePreset;
  from?: string;
  to?: string;
}

/**
 * The summary's two periods (§2.3–§2.4): the Rundown's — `allUpcoming` (the default) or a bound on how far forward the
 * scheduled part looks, `days` (1–90, as text) only with `nextDays` — and the Overview's, in one request.
 */
export interface RoomSummaryParams extends RoomOverviewPeriodParams {
  rundown?: 'allUpcoming' | 'today' | 'nextDays' | 'thisWeek' | 'thisMonth';
  days?: string;
}

/** Mirrors TournamentService.MaxPageSize. The server clamps regardless; this avoids a wasted round trip. */
export const MAX_PAGE_SIZE = 200;

/**
 * Mirrors FindRoomPlayersByCardRequest.MaxCards: the most cards `players.findByCards` may name. The server
 * refuses more (`too_many_cards`), since each is a parameter of one SQL statement; a caller sends batches.
 */
export const FIND_BY_CARDS_MAX = 1000;

/**
 * Mirrors FindRoomTournamentsByInternalIdRequest.MaxInternalIds: the most internal IDs
 * `tournaments.findByInternalIds` may name, which is the importer's own limit on a file (`MAX_TOURNAMENTS`).
 */
export const FIND_BY_INTERNAL_IDS_MAX = 500;

/**
 * Mirrors PlayerSearchTerm.MaxLength: the longest term a room's player search takes, the widest column any
 * of them matches (an email). The server refuses a longer one (`query_too_long`); a search box takes this as
 * its `maxLength`, so nobody typing meets the refusal. `limits.test.js` holds the two equal.
 */
export const PLAYER_SEARCH_MAX_LENGTH = 256;

/** The columns the server will order by. Anything else is a 400, not a silently unsorted list. */
export const TOURNAMENT_SORTS = ['date', 'name', 'buyIn', 'entries'] as const;
export type TournamentSort = (typeof TOURNAMENT_SORTS)[number];
export type SortDirection = 'asc' | 'desc';

export interface TournamentListParams {
  roomId?: string;
  /** Inclusive lower bound, YYYY-MM-DD. */
  from?: string;
  /** Inclusive upper bound, YYYY-MM-DD. */
  to?: string;
  /** AND semantics — a tournament must carry every tag listed. */
  tags?: string[];
  /**
   * The opposite quantifier to `tags`: any one of these drops a tournament. "NLH but not
   * satellites or seniors" vetoes on either, so each listed tag stands alone.
   *
   * A tag in both lists is a 400 — it can only ever match nothing.
   */
  excludeTags?: string[];
  /** 1-based. */
  page?: number;
  pageSize?: number;
  /** Defaults to `date`. Id always breaks ties, so the order is total across pages. */
  sort?: TournamentSort;
  /** Defaults to `desc` for `date`, `asc` for every other column. */
  dir?: SortDirection;
}

/** The date presets the room's list resolves on the server, against the room's gaming day. */
export const DATE_PRESETS = [
  'yesterday', 'today', 'tomorrow', 'lastWeek', 'thisWeek', 'nextWeek', 'lastMonth', 'thisMonth', 'nextMonth',
] as const;
export type DatePreset = (typeof DATE_PRESETS)[number];

/**
 * A room's list: the room is in the path, and a date range is either a `preset` — resolved by the
 * server at the room's gaming day, which the page echoes as `from`/`to` — or explicit dates, never both.
 */
export interface RoomTournamentListParams extends Omit<TournamentListParams, 'roomId'> {
  preset?: DatePreset;
  /**
   * Any one of these is enough — §3.2's quantifier for a scope's required tags, where `tags` requires every
   * one. The room list's alone: `GET /api/tournaments` and the bulk tag apply do not take it.
   */
  anyTags?: string[];
}

/**
 * Typed Brush API facade over the HTTP transport. All shapes come from the
 * generated OpenAPI schema (./generated/schema). List endpoints are unwrapped to
 * return the array directly.
 */
export function createApiClient(options: ApiClientOptions) {
  const http = createHttp(options);

  return {
    rooms: {
      /** Every room with its rollup counts. Staff only — see `directory` for the public one. */
      list: () => http.get<{ data: T.Room[] }>('/api/rooms').then((r) => r.data),

      /**
       * The public room directory: readable by anybody, signed in or not, and carrying nothing
       * about a person. This is the list a player chooses their room from before they hold a
       * card, so it is deliberately not the same call as `list`.
       *
       * Sent with a token like every other call, unlike `version()`. The endpoint permits
       * anonymous callers because the tier says it must, not because the SPA making this request
       * lacks a token — it has one, and dropping it here would only make this the one read whose
       * failures look different from the rest.
       */
      directory: () =>
        http.get<{ data: T.RoomDirectory[] }>('/api/rooms/directory').then((r) => r.data),
      /**
       * The room's "today" — its gaming day, computed by the server (ADR 0014) — and the instant the
       * next one begins, which is how long the answer stays true. Never the browser's calendar.
       */
      today: (roomId: string) => http.get<T.RoomToday>(`/api/rooms/${roomId}/today`),
      /**
       * What room-web's shell and Overview draw — the section counts, the tournament totals, the
       * rundown, the running leaderboards and the contests in flight — computed by the server at the
       * room's gaming day. Staff of the room only. With a period, `rundownWindow` and `period` are present; without,
       * the answer is what it always was.
       */
      summary: (roomId: string, params: RoomSummaryParams = {}) =>
        http.get<T.RoomSummary>(`/api/rooms/${roomId}/summary${qs({ ...params })}`),
      create: (body: T.CreateRoomRequest) => http.post<T.Room>('/api/rooms', body),
      patch: (id: string, body: T.PatchRoomRequest) => http.patch<T.Room>(`/api/rooms/${id}`, body),
      remove: (id: string) => http.delete<void>(`/api/rooms/${id}`),
    },

    players: {
      /**
       * The caller's own results at a room — the card their sign-in holds there, from the token — a page
       * newest first beside totals over every result. What the portal's Home and Stats read in place of the
       * room's capped tournament list (known gap 78). A caller with no card at the room, staff included, is
       * refused with `room_not_a_member`. Pages of 50 by default, at most 200.
       */
      ownResults: (roomId: string, page?: number, pageSize?: number) => {
        const qs = new URLSearchParams();
        if (page !== undefined) qs.set('page', String(page));
        if (pageSize !== undefined) qs.set('pageSize', String(pageSize));
        const query = qs.size > 0 ? `?${qs.toString()}` : '';
        return http.get<T.OwnResults>(`/api/rooms/${roomId}/players/me/results${query}`);
      },

      /** How many players the platform holds, for the one screen that wants a number. Admin. */
      count: () => http.get<T.PlayerCount>('/api/players/count'),

      /** One player, with no card for any caller (#494): the holder's own card is on `me`'s memberships. */
      get: (id: string) => http.get<T.PlayerProfile>(`/api/players/${id}`),
      /** A card is the room's own reference, so both halves are needed to name a player. */
      byCard: (roomId: string, cardId: string) =>
        http.get<T.Player>(`/api/rooms/${roomId}/players/by-card/${encodeURIComponent(cardId)}`),
      create: (body: T.CreatePlayerRequest) => http.post<T.Player>('/api/players', body),

      /**
       * The room's players holding any of `cardIds` — what the results import asks about its file's
       * cards. At most `FIND_BY_CARDS_MAX` cards a request; a caller with more sends batches. A card
       * the room does not hold is not in the answer. Staff of the room only.
       */
      findByCards: (roomId: string, cardIds: readonly string[]) =>
        http
          .post<{ data: T.PlayerSummary[] }>(`/api/rooms/${roomId}/players/by-cards`, { cardIds })
          .then((r) => r.data),

      /**
       * What committing one part of a players file would do, row by row, against the room's cards — writes
       * nothing. The browser reads the CSV and splits it; the server decides every outcome. Staff of the room only.
       */
      importPreview: (roomId: string, body: T.PreviewPlayerImportRequest) =>
        http.post<T.PlayerImportPreview>(`/api/rooms/${roomId}/players/import/preview`, body),

      /**
       * Writes one previewed part in one transaction, or nothing: 409 `import_file_changed` when the rows are not the
       * previewed ones, 409 `import_stale` when a card the part names changed since, 400 `import_has_errors`.
       */
      importCommit: (roomId: string, body: T.CommitPlayerImportRequest) =>
        http.post<T.PlayerImportResult>(`/api/rooms/${roomId}/players/import/commit`, body),

      /**
       * The room's players whose card, first name, last name, city or state contains `query`, by
       * surname, at most `limit` of them (the server caps it). A null or blank query answers the first
       * players by surname. Staff of the room only. A POST so the term, often a card number, stays out
       * of the URL.
       */
      searchRoom: (roomId: string, body: T.SearchRoomPlayersRequest) =>
        http
          .post<{ data: T.PlayerSummary[] }>(`/api/rooms/${roomId}/players/search`, body)
          .then((r) => r.data),

      /**
       * A page of the room's players with the contact the room holds, by surname, optionally narrowed by a
       * term matched in the card, either part of the name, the whole name and the email: the Players
       * screen. A POST so the term stays out of the URL, and one URL serves every page. 50 to a page by
       * default, never more than 200; staff only, room-scoped server-side.
       */
      records: (roomId: string, body: T.PageRoomPlayerRecordsRequest) =>
        http.post<T.RoomPlayerPage>(`/api/rooms/${roomId}/players/records`, body),

      /**
       * Edits the room's record of its own customer: the name on the card, where they are from,
       * and how to reach them. Not identity — the card ID and the room are not patchable.
       *
       * Null leaves a field alone, as everywhere else. Removing a contact needs `clearEmail` /
       * `clearPhone`: a blank string is refused, so a form field that happened to submit empty
       * cannot silently make a card unclaimable.
       */
      patch: (roomId: string, id: string, body: T.PatchPlayerRequest) =>
        http.patch<T.RoomPlayer>(`/api/rooms/${roomId}/players/${id}`, body),

      /** How many of a room's players can be sent a verification code. */
      contactCoverage: (roomId: string) =>
        http.get<T.PlayerContactCoverage>(`/api/rooms/${roomId}/players/contact-coverage`),
      /**
       * Name or card-ID search, scoped server-side to the rooms the caller may see and capped
       * in size. Used by the portal's player picker instead of loading every player.
       */
      search: (query?: string, limit?: number) =>
        http
          .get<{ data: T.Player[] }>(
            `/api/players/search${qs({ q: query, limit: limit === undefined ? undefined : String(limit) })}`,
          )
          .then((r) => r.data),
    },

    tournaments: {
      /**
       * Filtered, ordered and paged by the server. Dates are inclusive `YYYY-MM-DD` bounds and
       * `tags` is AND — a tournament must carry every tag listed.
       */
      list: (params: TournamentListParams = {}) =>
        http.get<T.Paged<T.TournamentRow>>(`/api/tournaments${tournamentQs(params)}`),
      /**
       * One tournament, results included, so it need not be found in a page of the list. Each result names its
       * finisher, so nothing else need be read to show who finished where. Another room's tournament is a 404,
       * exactly as an id naming nothing. **No card on it** (#494): members read this route. A staff screen reads
       * `staffDetail`.
       */
      get: (id: string) => http.get<T.TournamentDetail>(`/api/tournaments/${id}`),
      /**
       * The same tournament **with its cards** — each named finisher's and each result's source card — for the
       * room's own staff (#494): the read a detail view or a results editor wants. An operator of another room who
       * holds a card here is refused `room_not_operated`; a caller the room is closed to gets a 404.
       */
      staffDetail: (id: string) =>
        http.get<T.TournamentStaffDetail>(`/api/tournaments/${id}/staff-detail`),
      /**
       * A room's list as a list shape: the rows the table draws, without each tournament's results or
       * sittings — read those with `get`. Filtered, ordered and paged by the server as `list` is.
       */
      listForRoom: (roomId: string, params: RoomTournamentListParams = {}) =>
        http.get<T.TournamentPage>(`/api/rooms/${roomId}/tournaments${tournamentQs(params)}`),
      /**
       * The room's tag vocabulary: each tag its tournaments carry, with how many carry it, counted by
       * exact spelling. What a tag picker ranks by — never a count over a fetched list.
       */
      tagUsage: (roomId: string) =>
        http
          .get<{ data: T.TournamentTagUsage[] }>(`/api/rooms/${roomId}/tournament-tags`)
          .then((r) => r.data),
      /**
       * How many tournaments match — the server's own count of the filtered set, asked for with a
       * one-row page. Never the length of a fetched page, which stops at the maximum page size and would
       * report 200 for a platform holding 2,000 (known gap 6).
       */
      count: (roomId?: string) =>
        http
          .get<T.Paged<T.Tournament>>(`/api/tournaments${tournamentQs({ roomId, pageSize: 1 })}`)
          .then((r) => r.total),
      /**
       * Adds and removes tags across every tournament the filter matches, in one transaction on
       * the server.
       *
       * Server-side because the set spans pages: looping over a fetched page here would tag at most
       * the 200 rows it holds and report success for the rest. The filter fields are the list's
       * own, so what gets written is what was on screen.
       */
      applyTags: (body: T.ApplyTournamentTagsRequest) =>
        http.post<T.ApplyTournamentTagsResult>('/api/tournaments/apply-tags', body),
      /**
       * Claims a place nobody has claimed for a player, or returns a claimed place to unclaimed
       * (`playerId: null`). Names the result row the caller saw, so a place a re-import replaced
       * since is refused rather than claimed (ADR 0013).
       */
      assignPlace: (id: string, position: number, body: T.AssignPlacePlayerRequest) =>
        http.put<T.Tournament>(`/api/tournaments/${id}/results/${position}/player`, body),
      /**
       * The room's tournaments holding any of `internalIds`, sittings and tags included and no results — what
       * the tournament import compares its file against (known gap 108). At most `FIND_BY_INTERNAL_IDS_MAX`;
       * an ID the room does not hold is not in the answer. Staff of the room only.
       */
      findByInternalIds: (roomId: string, internalIds: readonly string[]) =>
        http
          .post<{ data: T.HeldTournament[] }>(`/api/rooms/${roomId}/tournaments/by-internal-ids`, { internalIds })
          .then((r) => r.data),
      /** Every place in a room's results that nobody has claimed yet, newest tournament first. */
      unclaimedPlaces: (roomId: string) =>
        http
          .get<{ data: T.UnclaimedPlace[] }>(`/api/rooms/${roomId}/unclaimed-places`)
          .then((r) => r.data),
      create: (body: T.CreateTournamentRequest) => http.post<T.Tournament>('/api/tournaments', body),
      patch: (id: string, body: T.PatchTournamentRequest) => http.patch<T.Tournament>(`/api/tournaments/${id}`, body),
      remove: (id: string) => http.delete<void>(`/api/tournaments/${id}`),
    },

    /**
     * Rating boards — `docs/specs/ratings.md`.
     *
     * The only ratings surface there is. `ratingGroups` — the legacy tag-filtered grouping, which
     * never scored anything — retired at Phase 4b (ADR 0011 Decision 13) and its five routes are
     * gone from the API; every row it held was converted to a board.
     */
    ratingBoards: {
      /**
       * The room's boards, plus every **platform template** (§2.3) — a board with a null `roomId`,
       * which appears in every room's picker and is cloned rather than used in place.
       */
      list: (roomId?: string) =>
        http.get<{ data: T.RatingBoard[] }>(`/api/rating-boards${qs({ roomId })}`).then((r) => r.data),
      get: (id: string) => http.get<T.RatingBoard>(`/api/rating-boards/${id}`),
      create: (body: T.CreateRatingBoardRequest) =>
        http.post<T.RatingBoard>('/api/rating-boards', body),
      update: (id: string, body: T.PutRatingBoardRequest) =>
        http.put<T.RatingBoard>(`/api/rating-boards/${id}`, body),
      remove: (id: string) => http.delete<void>(`/api/rating-boards/${id}`),

      /**
       * §2.4's Draft → Running, which is what turns the selected points system version into the
       * pin. The ordinary request is empty — the board already carries its selection — and naming
       * a system selects and pins in one go. A board with nothing selected is a 409
       * `points_system_required`, never a silent start.
       */
      start: (id: string, body: T.StartRatingBoardRequest = { pointsSystemId: null, pointsSystemVersion: null }) =>
        http.post<T.RatingBoard>(`/api/rating-boards/${id}/start`, body),

      /**
       * §1.1's steps 8–12, run at one anchor and answered. **Writes nothing** — snapshots are
       * Phase 5, so unlike a leaderboard's standings this is computed per read.
       *
       * `asOf` is §2.2's window anchor and defaults to today. It is not a detail: a rolling-window
       * rating changes when the calendar changes even though no result changed, so the same board
       * at two anchors is two different boards — which is the whole demonstration that a rating is
       * not a leaderboard. `yyyy-mm-dd` and only that; anything else is a 400 `invalid_date`.
       *
       * A POST although it persists nothing, matching the points preview: it is the most expensive
       * thing this feature does, and it keeps an intermediary from caching an answer whose subject
       * is that it moves.
       */
      compute: (id: string, asOf?: string) =>
        http.post<T.RatingStandings>(`/api/rating-boards/${id}/compute${qs({ asOf })}`, {}),

      /**
       * §4.7's audiences, listing the boards at one room they may actually read — a player's picker,
       * where staff use `list`.
       *
       * A minimal projection (`RatingBoardSummary`) rather than the configuration shape, and filtered
       * rather than refused: a reader asking about a room with nothing published gets an empty list,
       * because that is what they have found. Drafts are never offered, whatever visibility they
       * store — a Draft board reads as Private (§4.7).
       */
      visible: (roomId: string) =>
        http
          .get<{ data: T.RatingBoardSummary[] }>(
            `/api/rating-boards/visible${qs({ roomId })}`)
          .then((r) => r.data),

      /**
       * §7. Why this player has the rating they have — period by period, with every result that
       * bucketed in, **including the ones selection dropped**. A dropped row carries
       * `decayedScore: 0` and the engine's own `selectionNote`, because a player who cashed and
       * sees no trace of it reports a bug.
       *
       * Each row nests the points engine's per-event working verbatim, which is the same
       * `PointsBreakdownEventDto` a leaderboard breakdown and the designer's drill-down carry.
       *
       * **Read out of the latest snapshot, so there is no `asOf`** — it explains the standings
       * `standings` serves and nothing else. A live computation would answer about a different
       * board every day, because §8.1's anchor drift moves a rolling window whether or not a result
       * changed; the key to cache this under is the snapshot's `sequenceNumber`.
       *
       * **A member may only ask about their own card.** Staff read any player's on a board they can
       * see; anybody else gets 403 `breakdown_not_yours` (ADR 0011 Decision 10). The standings
       * publish everybody's rating; how another player earned theirs is theirs.
       *
       * Three absences, three answers: a board that has published nothing answers a null
       * `sequenceNumber`, a player absent from the snapshot answers a null `rank`, and a player id
       * that names nobody is a 404.
       */
      breakdown: (id: string, playerId: string) =>
        http.get<T.RatingBreakdown>(
          `/api/rating-boards/${id}/players/${playerId}/breakdown`),

      // ---- §8: snapshots, recalculation and drift ------------------------------------------

      /**
       * §8's standings: **the latest snapshot, read back.**
       *
       * Deliberately a different call from `compute`, which runs the engine. One answers what this
       * rating *does* say — the figures that were frozen, and the anchor they were measured
       * against — and the other what it *would* say if somebody recalculated now. Collapsing them
       * is how a published standing starts moving under the player reading it.
       *
       * <b>Always answers a record.</b> A board that has never been recalculated comes back with a
       * null `sequenceNumber` and an empty `data` — it has published nothing, which is not the same
       * as nobody having scored, and the two send a reader to different places. That is the whole
       * reason the response is not a nullable snapshot: a null body cannot be said over HTTP here
       * (ASP.NET's JSON writer skips it, leaving a 200 with no body, which the transport can only
       * treat as a broken response). A draft can never have a snapshot at all — recalculating and
       * closing both refuse one — so on a draft this is a call worth not making.
       */
      standings: (id: string) =>
        http.get<T.RatingBoardStandings>(`/api/rating-boards/${id}/standings`),

      /**
       * The same published standings **with each player's card**, for the board's own room staff (gap 131): the
       * anonymous `standings` carries none. Same record, same "nothing published" shape.
       */
      staffStandings: (id: string) =>
        http.get<T.RatingBoardStaffStandings>(`/api/rating-boards/${id}/staff-standings`),

      /**
       * §8's recalculate-now, and §8.2's override on a closed board.
       *
       * The trigger is decided from the board's **status**, never from whether a reason was sent —
       * so an ordinary recalculation may carry a note without being treated as an override, and an
       * administrator recalculating a closed board must supply one.
       *
       * `asOf` sets §2.2's anchor and is not a test hook: recomputing an awards-night rating a week
       * later, and backfilling a schedule that was missed, both legitimately produce a snapshot
       * whose anchor is not today.
       */
      recalculate: (id: string, reason?: string | null, asOf?: string) =>
        http.post<T.RatingSnapshot>(
          `/api/rating-boards/${id}/recalculate${qs({ asOf })}`, { reason: reason ?? null }),

      /** §2.5's Running → Closed, which writes the final snapshot. */
      close: (id: string) => http.post<T.RatingBoard>(`/api/rating-boards/${id}/close`, {}),

      /**
       * §2.2's recalculation trigger. A **null `recalcCron` under `scheduled` is not "no
       * schedule"** — it is locked rule 4's inheritance, meaning the platform's cadence resolved
       * when the rating runs. Refused on a closed board for everybody, administrators included.
       */
      setRecalculation: (id: string, body: T.PatchRatingRecalcRequest) =>
        http.patch<T.RatingBoard>(`/api/rating-boards/${id}/recalculation`, body),

      /** The audit trail, newest first and without rows. */
      snapshots: (id: string) =>
        http.get<{ data: T.RatingSnapshot[] }>(`/api/rating-boards/${id}/snapshots`)
          .then((r) => r.data),

      snapshot: (id: string, sequenceNumber: number) =>
        http.get<T.RatingSnapshot>(`/api/rating-boards/${id}/snapshots/${sequenceNumber}`),

      /**
       * §8.1's **two-dimensional** drift, admin only.
       *
       * `hasDataDrift` and `hasAnchorDrift` are separate answers and there is no combined flag: a
       * rating can be stale because the calendar moved with no result having changed, and the
       * operator's response differs — data drift may mean an import wants checking, anchor drift
       * just means recalculate.
       */
      drift: (id: string, asOf?: string) =>
        http.get<T.RatingDrift>(`/api/rating-boards/${id}/drift${qs({ asOf })}`),
    },

    leaderboards: {
      list: (roomId?: string) =>
        http.get<{ data: T.Leaderboard[] }>(`/api/leaderboards${qs({ roomId })}`).then((r) => r.data),
      create: (body: T.CreateLeaderboardRequest) => http.post<T.Leaderboard>('/api/leaderboards', body),
      update: (id: string, body: T.PutLeaderboardRequest) => http.put<T.Leaderboard>(`/api/leaderboards/${id}`, body),
      remove: (id: string) => http.delete<void>(`/api/leaderboards/${id}`),
      /**
       * §7.5. The **latest snapshot**, read back — not a live computation.
       *
       * Returns the whole envelope rather than unwrapping `data`, unlike every other list here:
       * the rows alone cannot say how old they are, and a snapshot can be days stale while looking
       * exactly like current standings. `sequenceNumber` is null when the board has published
       * nothing, which is not the same as nobody having scored.
       */
      standings: (id: string) =>
        http.get<T.LeaderboardStandings>(`/api/leaderboards/${id}/standings`),

      /**
       * The same published standings **with each player's card**, for the board's own room staff (#494): the
       * member `standings` carry none. Same envelope, same "nothing published" shape. An operator of another room
       * who holds a card here is refused `room_not_operated`; a caller the room is closed to gets a 404.
       */
      staffStandings: (id: string) =>
        http.get<T.LeaderboardStaffStandings>(`/api/leaderboards/${id}/staff-standings`),

      /**
       * §7.4. Why this player has the points they have, event by event.
       *
       * Read back from the **same snapshot** `standings` serves, so the total here is the total
       * there — it is not a live computation, and asking twice in a row cannot produce two
       * different explanations of one number. Each row carries an `explanation` written in plain
       * English by the engine at the moment the figures were computed; a client shows it rather
       * than assembling its own, since the deal rule that makes a winner score third-place points
       * is exactly the sentence three clients would word three ways.
       *
       * `sequenceNumber` null means the board has published nothing. A null `rank` with a
       * sequence number present means this player is not on it — two states worth wording
       * differently.
       */
      breakdown: (id: string, playerId: string) =>
        http.get<T.LeaderboardBreakdown>(`/api/leaderboards/${id}/players/${playerId}/breakdown`),

      /**
       * §2.3's pin, taken here and never again. The system is chosen at this moment rather than
       * stored on the draft, because until a board starts the choice has no consequence — and
       * after it starts, changing it would rewrite standings somebody has been paid against.
       *
       * `version` defaults to the system's newest published one. A draft is refused: a draft can
       * still be edited, and the pin exists precisely so that what computes cannot.
       */
      start: (id: string, body: T.StartLeaderboardRequest) =>
        http.post<T.Leaderboard>(`/api/leaderboards/${id}/start`, body),

      /** Writes the final snapshot — the one that determines prizes (§8). */
      close: (id: string) => http.post<T.Leaderboard>(`/api/leaderboards/${id}/close`),

      /** §9.9's one control that stays editable while a board runs. Refused once it is closed. */
      setRecalculation: (id: string, body: T.PatchLeaderboardRecalcRequest) =>
        http.patch<T.Leaderboard>(`/api/leaderboards/${id}/recalculation`, body),

      /**
       * Recalculate now, writing a snapshot. `reason` is ignored on a running board and
       * **required** on a closed one, where an administrator is overriding §8.2's block.
       */
      recalculate: (id: string, body: T.RecalculateLeaderboardRequest = { reason: null }) =>
        http.post<T.LeaderboardSnapshot>(`/api/leaderboards/${id}/recalculate`, body),

      snapshots: (id: string) =>
        http.get<T.LeaderboardSnapshotList>(`/api/leaderboards/${id}/snapshots`),
      snapshot: (id: string, sequenceNumber: number) =>
        http.get<T.LeaderboardSnapshot>(`/api/leaderboards/${id}/snapshots/${sequenceNumber}`),

      /**
       * §8.1. Computes live and diffs against the latest snapshot; any difference means the
       * underlying data moved since the last recalculation.
       *
       * **Admin-only on the server**, which §8.1 asks for in as many words ("surface drift as an
       * admin alert"). A room operator calling it gets a 403, so a client shows the indicator only
       * to an administrator rather than letting every operator's leaderboard screen fail a request.
       */
      drift: (id: string) => http.get<T.LeaderboardDrift>(`/api/leaderboards/${id}/drift`),
    },

    fantasyContests: {
      list: (roomId?: string) =>
        http.get<{ data: T.FantasyContest[] }>(`/api/fantasy-contests${qs({ roomId })}`).then((r) => r.data),
      get: (id: string) => http.get<T.FantasyContest>(`/api/fantasy-contests/${id}`),
      create: (body: T.CreateFantasyContestRequest) => http.post<T.FantasyContest>('/api/fantasy-contests', body),
      /** 409 unless the contest is still a draft — the rules an entrant drafted under do not move. */
      patch: (id: string, body: T.PatchFantasyContestRequest) => http.patch<T.FantasyContest>(`/api/fantasy-contests/${id}`, body),
      /** 409 once any team exists. Close it instead. */
      remove: (id: string) => http.delete<void>(`/api/fantasy-contests/${id}`),

      pool: {
        get: (id: string) => http.get<T.FantasyPool>(`/api/fantasy-contests/${id}/pool`),
        /** 409 unless Draft. Publishing is what freezes the prices entrants draft against. */
        put: (id: string, body: T.PutFantasyPoolRequest) =>
          http.put<T.FantasyPool>(`/api/fantasy-contests/${id}/pool`, body),
        /** Runs the salary model and answers with a proposal. Writes nothing. */
        derive: (id: string, body: T.DeriveFantasyPoolRequest) =>
          http.post<T.SalaryPoolPreview>(`/api/fantasy-contests/${id}/pool/derive`, body),
      },

      /**
       * §6's transitions. Each is its own route rather than a status field: publish pins the salary
       * model version and computes `locksAt`, lock freezes the scope and stamps every team, close
       * writes the final standings.
       */
      publish: (id: string, body: T.PublishFantasyContestRequest) =>
        http.post<T.FantasyContest>(`/api/fantasy-contests/${id}/publish`, body),
      lock: (id: string) => http.post<T.FantasyContest>(`/api/fantasy-contests/${id}/lock`, {}),
      close: (id: string) => http.post<T.FantasyContest>(`/api/fantasy-contests/${id}/close`, {}),
      recalculate: (id: string, body: T.RecalculateFantasyContestRequest) =>
        http.post<T.FantasyStandingSnapshot>(`/api/fantasy-contests/${id}/recalculate`, body),

      standings: (id: string) =>
        http.get<T.FantasyStandings>(`/api/fantasy-contests/${id}/standings`),
      snapshots: (id: string) =>
        http
          .get<{ data: T.FantasyStandingSnapshot[] }>(`/api/fantasy-contests/${id}/snapshots`)
          .then((r) => r.data),
      snapshot: (id: string, seq: number) =>
        http.get<T.FantasyStandingSnapshot>(`/api/fantasy-contests/${id}/snapshots/${seq}`),

      /**
       * §6.2's scope drift, **admin only** — an operator gets a 403, so show the indicator only to
       * an administrator rather than letting every operator's screen fail a request.
       */
      drift: (id: string) => http.get<T.FantasyContestDrift>(`/api/fantasy-contests/${id}/drift`),

      /**
       * §6.2's **Adopt**, admin only and behind the same policy as the report: take the board's
       * live event list as this contest's and rescore under it. `reason` is required — this
       * rewrites standings entrants made irreversible decisions against.
       *
       * There is no `hold`. Hold is what already happens: the projection skips a drifted contest,
       * so its standings do not move. A call confirming the status quo would change nothing while
       * reading as though something had been settled.
       */
      adoptScope: (id: string, body: T.AdoptFantasyScopeRequest) =>
        http.post<T.FantasyStandingSnapshot>(`/api/fantasy-contests/${id}/adopt-scope`, body),
    },

    fantasyTeams: {
      /**
       * Teams the caller may see in one contest: staff get every entry, anybody else gets the ones
       * on their own cards. There is deliberately no `userId` — the server scopes this from the
       * token, which is what closed the read half of known gap 1 (ADR 0010, Decision 5). Do not add
       * one back.
       */
      list: (contestId: string) =>
        http
          .get<{ data: T.FantasyTeam[] }>(`/api/fantasy-contests/${contestId}/teams`)
          .then((r) => r.data),
      /**
       * Upsert on (contest, entrant, entryIndex): 200 replaced, 201 created. The entrant is the
       * caller's own card at the contest's room, resolved server-side — the request body carries
       * no entrant id and must not grow one.
       */
      upsert: (contestId: string, body: T.CreateFantasyTeamRequest) =>
        http.post<T.FantasyTeam>(`/api/fantasy-contests/${contestId}/teams`, body),
      /** 409 once the contest locks. */
      put: (teamId: string, body: T.PutFantasyTeamRequest) =>
        http.put<T.FantasyTeam>(`/api/fantasy-teams/${teamId}`, body),
      /** 409 once the contest locks — by then the entry is a standing. */
      remove: (teamId: string) => http.delete<void>(`/api/fantasy-teams/${teamId}`),
      /**
       * The same rules the write enforces, answered rather than thrown, and persisting nothing.
       * The contest-scoped form is what a lineup builder wants: it works before the first entry
       * exists, which is exactly when an entrant is learning what the budget will bear.
       */
      validate: (contestId: string, body: T.PutFantasyTeamRequest) =>
        http.post<T.FantasyRosterValidation>(
          `/api/fantasy-contests/${contestId}/teams/validate`,
          body,
        ),
      validateEntry: (teamId: string, body: T.PutFantasyTeamRequest) =>
        http.post<T.FantasyRosterValidation>(`/api/fantasy-teams/${teamId}/validate`, body),
      /** §8.4's audit trail. Player-visible, and read from the frozen snapshot rather than recomputed. */
      breakdown: (teamId: string) =>
        http.get<T.FantasyTeamBreakdown>(`/api/fantasy-teams/${teamId}/breakdown`),
    },

    /**
     * Salary models (fantasy-contests.md §8.1) — the same catalogue shape as `pointsSystems`,
     * with a contest pinning a version instead of a leaderboard.
     */
    salaryModels: {
      listForRoom: (roomId: string, includeArchived = false) =>
        http
          .get<{ data: T.SalaryModel[] }>(
            `/api/rooms/${roomId}/salary-models${qs({ includeArchived: includeArchived ? 'true' : undefined })}`,
          )
          .then((r) => r.data),
      listPlatform: (includeArchived = false) =>
        http
          .get<{ data: T.SalaryModel[] }>(
            `/api/salary-models${qs({ includeArchived: includeArchived ? 'true' : undefined })}`,
          )
          .then((r) => r.data),
      get: (id: string) => http.get<T.SalaryModel>(`/api/salary-models/${id}`),
      createForRoom: (roomId: string, body: T.CreateSalaryModelRequest) =>
        http.post<T.SalaryModel>(`/api/rooms/${roomId}/salary-models`, body),
      createPlatform: (body: T.CreateSalaryModelRequest) =>
        http.post<T.SalaryModel>('/api/salary-models', body),
      patch: (id: string, body: T.PatchSalaryModelRequest) =>
        http.patch<T.SalaryModel>(`/api/salary-models/${id}`, body),
      remove: (id: string) => http.delete<void>(`/api/salary-models/${id}`),
      clone: (id: string, body: T.CloneSalaryModelRequest) =>
        http.post<T.SalaryModel>(`/api/salary-models/${id}/clone`, body),
      archive: (id: string) => http.post<T.SalaryModel>(`/api/salary-models/${id}/archive`, {}),
      unarchive: (id: string) => http.post<T.SalaryModel>(`/api/salary-models/${id}/unarchive`, {}),

      versions: {
        list: (id: string) =>
          http
            .get<{ data: T.SalaryModelVersion[] }>(`/api/salary-models/${id}/versions`)
            .then((r) => r.data),
        get: (id: string, n: number) =>
          http.get<T.SalaryModelVersion>(`/api/salary-models/${id}/versions/${n}`),
        create: (id: string, body: T.CreateSalaryModelVersionRequest) =>
          http.post<T.SalaryModelVersion>(`/api/salary-models/${id}/versions`, body),
        /**
         * A published version refuses a CHANGED definition and still takes the test scope beside
         * it — a PUT carries the whole resource, so send back what you read.
         */
        put: (id: string, n: number, body: T.PutSalaryModelVersionRequest) =>
          http.put<T.SalaryModelVersion>(`/api/salary-models/${id}/versions/${n}`, body),
        remove: (id: string, n: number) =>
          http.delete<void>(`/api/salary-models/${id}/versions/${n}`),
        publish: (id: string, n: number) =>
          http.post<T.SalaryModelVersion>(`/api/salary-models/${id}/versions/${n}/publish`, {}),
        archive: (id: string, n: number) =>
          http.post<T.SalaryModelVersion>(`/api/salary-models/${id}/versions/${n}/archive`, {}),
      },

      /**
       * Prices a pool from a definition that has not been saved. Rate-limited on the same bucket as
       * the points-system preview — same kind of read, same debounced editor.
       */
      preview: (body: T.PreviewSalaryModelRequest) =>
        http.post<T.SalaryPoolPreview>('/api/salary-models/preview', body),
    },

    platformTags: {
      list: () => http.get<{ data: string[] }>('/api/platform-tags').then((r) => r.data),
      add: (tag: string) => http.post<{ data: string[] }>('/api/platform-tags', { tag }).then((r) => r.data),
      /**
       * The whole answer, not only the list: `draftBoards` is the warning a screen owes the
       * administrator. Refused with a 409 while a running board's scope names the tag.
       */
      remove: (tag: string) =>
        http.delete<T.PlatformTagRemoval>(`/api/platform-tags/${encodeURIComponent(tag)}`),
      /**
       * How many tournaments carry each platform tag, every room counted, matched without regard to case
       * (known gap 92). Administrators only.
       */
      usage: () =>
        http.get<{ data: T.PlatformTagUsage[] }>('/api/platform-tags/usage').then((r) => r.data),
    },

    /**
     * The platform's template catalogue. Readable by any signed-in caller — a room cannot
     * supply content for a template it can't see — but only administrators may change it.
     */
    templates: {
      list: () => http.get<{ data: T.Template[] }>('/api/templates').then((r) => r.data),
      create: (body: T.CreateTemplateRequest) => http.post<T.Template>('/api/templates', body),
      // PUT, not PATCH: the field list is replaced wholesale, and `kind` is not editable.
      update: (id: string, body: T.PutTemplateRequest) =>
        http.put<T.Template>(`/api/templates/${id}`, body),
      remove: (id: string) => http.delete<void>(`/api/templates/${id}`),
    },

    /**
     * One room's content for the catalogue. Room-scoped on the server: RoomScopeGuard runs
     * inside the service, so an operator cannot reach a room they are not assigned to.
     */
    roomTemplates: {
      /** Every template, each paired with this room's content for it (null when unfilled). */
      list: (roomId: string) =>
        http.get<{ data: T.RoomTemplate[] }>(`/api/rooms/${roomId}/templates`).then((r) => r.data),
      save: (roomId: string, templateId: string, body: T.PutRoomTemplateContentRequest) =>
        http.put<T.RoomTemplateContent>(
          `/api/rooms/${roomId}/templates/${templateId}/content`,
          body,
        ),
      clear: (roomId: string, templateId: string) =>
        http.delete<void>(`/api/rooms/${roomId}/templates/${templateId}/content`),
    },

    /**
     * How a leaderboard turns finishes into points — the catalogue, its versions, and the
     * preview that runs one without saving it.
     *
     * Two things about the shapes here are worth knowing before using them. A definition is
     * `unknown` on the wire: the API models it as an opaque JSON document (it is stored verbatim
     * and must read back byte for byte years later), so the OpenAPI schema has nothing to say
     * about its interior and neither does this facade. `@brush/ui` carries the TypeScript
     * mirror of the published JSON Schema for the screens that edit one.
     *
     * And ownership comes from the *route*, never from a body: `createPlatform` makes a system
     * owned by nobody's room, `createForRoom` one owned by that room, and there is no field a
     * caller could set to claim a room they don't operate.
     */
    pointsSystems: {
      /** Every platform system. Admin-only on the server. */
      listPlatform: (includeArchived = false) =>
        http
          .get<{ data: T.PointsSystem[] }>(
            `/api/points-systems${qs({ includeArchived: includeArchived ? 'true' : undefined })}`,
          )
          .then((r) => r.data),
      /**
       * What this room may choose from: its own systems plus every platform one, in a single
       * ordered list rather than two the client has to merge.
       */
      listForRoom: (roomId: string, includeArchived = false) =>
        http
          .get<{ data: T.PointsSystem[] }>(
            `/api/rooms/${roomId}/points-systems${qs({
              includeArchived: includeArchived ? 'true' : undefined,
            })}`,
          )
          .then((r) => r.data),
      get: (id: string) => http.get<T.PointsSystem>(`/api/points-systems/${id}`),
      createPlatform: (body: T.CreatePointsSystemRequest) =>
        http.post<T.PointsSystem>('/api/points-systems', body),
      createForRoom: (roomId: string, body: T.CreatePointsSystemRequest) =>
        http.post<T.PointsSystem>(`/api/rooms/${roomId}/points-systems`, body),
      /** Name and description only. The definition lives on a version and is written through one. */
      patch: (id: string, body: T.PatchPointsSystemRequest) =>
        http.patch<T.PointsSystem>(`/api/points-systems/${id}`, body),
      /**
       * A copy this room owns and may edit. Lands as version 1, `draft`, whatever it was copied
       * from, and takes the source's newest *published* version unless `version` says otherwise.
       */
      clone: (id: string, body: T.ClonePointsSystemRequest) =>
        http.post<T.PointsSystem>(`/api/points-systems/${id}/clone`, body),
      /** 409 when any version is pinned by a leaderboard; the message leads with `archive`. */
      remove: (id: string) => http.delete<void>(`/api/points-systems/${id}`),
      archive: (id: string) => http.post<T.PointsSystem>(`/api/points-systems/${id}/archive`),
      unarchive: (id: string) => http.post<T.PointsSystem>(`/api/points-systems/${id}/unarchive`),

      // Versions are addressed by number, not id: that is what an operator reads and what the
      // routes name.
      versions: (id: string) =>
        http
          .get<{ data: T.PointsSystemVersion[] }>(`/api/points-systems/${id}/versions`)
          .then((r) => r.data),
      version: (id: string, n: number) =>
        http.get<T.PointsSystemVersion>(`/api/points-systems/${id}/versions/${n}`),
      /** A new draft. `copyFromVersion` is how a published version is edited — see `putVersion`. */
      createVersion: (id: string, body: T.CreatePointsSystemVersionRequest) =>
        http.post<T.PointsSystemVersion>(`/api/points-systems/${id}/versions`, body),
      /**
       * Saves a draft in place. A published version answers 409 rather than silently forking:
       * a new version is what a leaderboard will pin, so it is something an operator should have
       * asked for.
       */
      putVersion: (id: string, n: number, body: T.PutPointsSystemVersionRequest) =>
        http.put<T.PointsSystemVersion>(`/api/points-systems/${id}/versions/${n}`, body),
      removeVersion: (id: string, n: number) =>
        http.delete<void>(`/api/points-systems/${id}/versions/${n}`),
      /** The freeze. Irreversible, and validated once more on the way through. */
      publishVersion: (id: string, n: number) =>
        http.post<T.PointsSystemVersion>(`/api/points-systems/${id}/versions/${n}/publish`),
      archiveVersion: (id: string, n: number) =>
        http.post<T.PointsSystemVersion>(`/api/points-systems/${id}/versions/${n}/archive`),

      /**
       * Runs a definition over a room's real results and returns the standings it would produce.
       * Persists nothing — the definition rides in the body and need never have been saved.
       *
       * Rate-limited per caller (60 a minute by default), which is why the designer debounces
       * and caches on the definition rather than calling per keystroke.
       */
      preview: (body: T.PreviewPointsSystemRequest) =>
        http.post<T.PointsPreview>('/api/points-systems/preview', body),

      /**
       * §9.9's live count, and the events behind it. Step 1 of the pipeline alone: it takes no
       * definition, which is what lets a leaderboard show "21 events in scope" before it has
       * picked a system.
       *
       * Under the room rather than a leaderboard because the scope is one the operator is still
       * typing. Shares `preview`'s rate limit — the same read, driven by the same debounce.
       */
      scopeEvents: (roomId: string, scope: unknown) =>
        http.post<T.PointsScopeEvents>(`/api/rooms/${roomId}/points-scope/events`, { scope }),
    },

    /**
     * The workforce directory (ADR 0001's staff plane, never the players' one). Admin-only on the
     * server, and more tightly gated than anything else here: it answers with names and work
     * addresses of people who may have nothing to do with this platform.
     *
     * A failure here is usually not this app's fault — a Graph throttle, a missing consent, an
     * outage — and arrives as a 502 whose `code` `describeApiError` recognises, so the reason
     * reaches the screen instead of "the server had a problem".
     */
    directory: {
      searchUsers: (q: string, limit?: number) =>
        http.get<T.DirectorySearchResponse>(
          `/api/directory/users${qs({ q, limit: limit?.toString() })}`,
        ),
    },

    /** Platform administration: who may operate which room. Admin-only on the server. */
    roomOperators: {
      list: (roomId?: string) =>
        http.get<T.RoomOperatorList>(`/api/room-operators${qs({ roomId })}`),
      /**
       * Links a staged set in one act. All-or-nothing: if any entry is refused nothing is
       * written, and the refusal names every offender, so a screen can show them all at once
       * rather than making an administrator resubmit per problem.
       */
      bulkAssign: (body: T.BulkAssignRoomOperatorsRequest) =>
        http.post<T.RoomOperatorList>('/api/room-operators/bulk', body).then((r) => r.data),
      /**
       * Renames ONE link. The name belongs to the room's row and never to the person, so this
       * cannot reach the same operator's name at another room.
       */
      rename: (id: string, body: T.RenameRoomOperatorRequest) =>
        http.patch<T.RoomOperator>(`/api/room-operators/${id}`, body),
      remove: (id: string) => http.delete<void>(`/api/room-operators/${id}`),
    },

    /**
     * Connecting a sign-in to a player card (ADR 0010). Two calls serve every room and every kind
     * of proof — a room bringing its own authenticator adds a verifier server-side, not a flow
     * here.
     */
    memberships: {
      /**
       * Starts a claim. Always resolves the same way whether or not the contact matched anybody,
       * and the answer is not a receipt: see `ClaimChallenge`. A screen that says "check your
       * inbox" is the honest wording; "we've sent you a code" is not.
       */
      startClaim: (body: T.StartClaimRequest) =>
        http.post<T.ClaimChallenge>('/api/memberships/claims', body),

      /**
       * Finishes it with the code. 400 `invalid_code` (with attempts remaining in the message),
       * 410 for a challenge that expired or was already used, 409 if the card was claimed in the
       * meantime.
       */
      completeClaim: (challengeId: string, body: T.CompleteClaimRequest) =>
        http.post<T.Membership>(`/api/memberships/claims/${challengeId}/complete`, body),

      /**
       * Disconnects the caller from their card at one room — one row, and nothing else. The card
       * and everything it has been paid against stay the room's.
       *
       * Keyed on the room because the other half is the caller's own token: there is no
       * membership id to pass, deliberately, since a route taking one would invite passing
       * somebody else's.
       */
      unlink: (roomId: string) => http.delete<void>(`/api/me/memberships/${roomId}`),

      /** The room's own view: who holds its cards. Room-scoped server-side. */
      listForRoom: (roomId: string) =>
        http
          .get<{ data: T.RoomMembership[] }>(`/api/rooms/${roomId}/memberships`)
          .then((r) => r.data),

      /**
       * Takes one card back from the account holding it. The same row `unlink` removes, and a
       * different act: one is a person disconnecting themselves, the other a decision about
       * somebody else.
       */
      revoke: (roomId: string, playerId: string) =>
        http.delete<void>(`/api/rooms/${roomId}/memberships/${playerId}`),
    },

    /**
     * Who the caller is, per the API. Drives client-side access gating and room scoping — and
     * carries `memberships`, which is what player-web decides between the portal, a room switcher
     * and the discovery view with.
     */
    me: () => http.get<T.CurrentUser>('/api/me'),

    account: {
      /**
       * Erases the caller's own account (ADR 0012 step 28). `freshToken` is the token from a sign-in made
       * just now (`useFreshSignIn`) and is sent instead of the cached one, because the server refuses a
       * sign-in older than a few minutes with 403 `sign_in_again_required`. 404 `nothing_to_erase` when
       * Brush holds nothing for this sign-in.
       */
      erase: (freshToken: string) => http.delete<void>('/api/me/account', { token: freshToken }),
    },
    activity: {
      /** The admin Overview's activity cards. Administrators only. */
      overview: () => http.get<T.AdminOverview>('/api/activity/overview'),
      /** The operator Overview's activity cards for one room: its own figures, counts only. Its staff, and administrators. */
      room: (roomId: string, params: RoomOverviewPeriodParams = {}) =>
        http.get<T.RoomActivity>(`/api/rooms/${roomId}/activity${qs({ ...params })}`),
      /**
       * Engagement from this app — sessions and screens viewed. Use the tracker
       * (`activityTrackerFor`), which deduplicates and batches, rather than calling this directly.
       */
      record: (events: T.ActivityEventRequest[]) =>
        http.post<T.RecordActivityResponse>('/api/activity/events', { events }),
      /** Retention and engagement-type overrides. Administrators only (ADR 0012 step 25). */
      settings: () => http.get<T.ActivitySettings>('/api/activity/settings'),
      putRetention: (body: T.PutActivityRetentionRequest) =>
        http.put<T.ActivityRetention>('/api/activity/settings/retention', body),
      /** What the platform retention `body` would delete on the next purge. Counts; deletes nothing. */
      retentionImpact: (body: T.PutActivityRetentionRequest) =>
        http.post<T.RetentionImpact>('/api/activity/settings/retention/impact', body),
      putRoomRetention: (roomId: string, body: T.PutActivityRetentionRequest) =>
        http.put<T.ActivityRoomRetention>(`/api/activity/rooms/${roomId}/retention`, body),
      /** What a room's retention `body` would delete from that room on the next purge. */
      roomRetentionImpact: (roomId: string, body: T.PutActivityRetentionRequest) =>
        http.post<T.RetentionImpact>(`/api/activity/rooms/${roomId}/retention/impact`, body),
      /** An operator by a directory result's object id, a player by card at a room; never an account id. */
      putOverride: (body: T.PutActivityOverrideRequest) =>
        http.put<T.ActivityTypeOverride>('/api/activity/overrides', body),
      removeOverride: (id: string) => http.delete<void>(`/api/activity/overrides/${id}`),
      /** Erasures whose sign-in is still in the tenant, oldest first. Administrators only. */
      erasures: () =>
        http.get<{ data: T.PendingSignInRemoval[] }>('/api/activity/erasures').then((r) => r.data),
      /** Records that an erasure's sign-in was deleted from the tenant. Marking one twice records it once. */
      markSignInRemoved: (replacementId: string) =>
        http.post<void>(`/api/activity/erasures/${replacementId}/sign-in-removed`),
    },

    /**
     * Which build the API is running, for the About panel.
     *
     * Not under `/api` — it is a sibling of `/health`, an operational endpoint rather than part of
     * the product surface. Unauthenticated by way of `getAnonymous`, matching how the server
     * serves it; see the note there for why acquiring a token would defeat the purpose.
     */
    version: () => http.getAnonymous<T.ApiVersion>('/version'),

    /**
     * The seven exports (docs/specs/export.md §4) as files: `download` reads a 2xx as a Blob named by the server, and a
     * refusal as the same ApiError any call raises (EX25, EX26). `fallbackName` is used only when the response carries
     * no `Content-Disposition`.
     */
    exports: {
      download: (target: ExportTarget, choice: ExportChoice, fallbackName: string) =>
        http.download(exportPath(target, choice), fallbackName),
    },

    platformSettings: {
      get: () => http.get<T.PlatformSettings>('/api/platform-settings'),
      patch: (body: T.PatchPlatformSettingsRequest) => http.patch<T.PlatformSettings>('/api/platform-settings', body),
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
